<mat-dialog-content>
    <!-- Backdrop clicks and ESC are disabled application-wide, so this button and the one in
         the footer are the only ways out of the dialog. -->
    <button type="button" class="close" (click)="close()" aria-label="Close">
        <span aria-hidden="true">&times;</span>
    </button>

    <h4 mat-dialog-title translate>matchBankStatement.title</h4>

    <!-- What is being matched -->
    <div class="mb-3">
        <span class="font-weight-bold" translate>matchBankStatement.statement</span>:
        {{data.reference}} &mdash; {{data.amount | number: '1.2-2'}} {{data.currency}}
    </div>

    <!-- Linked invoices -->
    <div class="border rounded p-2 mb-3">
        <h6 class="font-weight-bold" translate>matchBankStatement.linkedInvoices</h6>

        <div *ngIf="matches.length === 0" class="text-muted" translate>matchBankStatement.noneLinked</div>

        <table class="table table-sm table-bordered mb-1" *ngIf="matches.length > 0">
            <thead>
                <tr>
                    <th translate>matchBankStatement.invoiceNumber</th>
                    <th class="text-right" translate>matchBankStatement.allocated</th>
                    <th translate>matchBankStatement.matchSource</th>
                    <th translate>matchBankStatement.matchedBy</th>
                    <th class="text-center" translate>matchBankStatement.actions</th>
                </tr>
            </thead>
            <tbody>
                <tr *ngFor="let match of matches">
                    <td>{{match.invoiceNumber}}</td>
                    <td class="text-right text-nowrap">{{match.allocatedAmount | number: '1.2-2'}}</td>
                    <td>{{match.matchSource}}</td>
                    <td>{{match.matchedByName}}</td>
                    <td class="text-center">
                        <button class="btn btn-sm btn-danger" type="button" [disabled]="working"
                            data-toggle="tooltip" data-placement="bottom"
                            title="{{'matchBankStatement.unlink' | translate}}"
                            (click)="unlink(match)">
                            <i class="fa fa-unlink"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Warnings, not blocks: an operator may well be recording a genuine part payment. -->
        <div class="alert alert-warning py-1 mb-1" *ngIf="matches.length > 0 && !totalsAgree">
            {{'matchBankStatement.totalMismatch' | translate}}
            {{linkedTotal | number: '1.2-2'}} / {{data.amount | number: '1.2-2'}} {{data.currency}}
        </div>

        <div class="alert alert-warning py-1 mb-0" *ngIf="overAllocated.length > 0">
            <span translate>matchBankStatement.overAllocated</span>
            <span *ngFor="let match of overAllocated; let last = last">
                {{match.invoiceNumber}} ({{match.totalAllocatedToInvoice | number: '1.2-2'}}
                / {{match.invoiceTotalAmount | number: '1.2-2'}}){{last ? '' : ', '}}
            </span>
        </div>
    </div>

    <!-- Search -->
    <form [formGroup]="searchForm">
        <div class="input-group mb-2">
            <div class="input-group-prepend">
                <span class="input-group-text" translate>matchBankStatement.invoiceNumber</span>
            </div>
            <input type="text" class="form-control d-inline" (keyup.enter)="search()" formControlName="invoiceNumber" appTrimWhitespace>

            <div class="input-group-prepend ml-2">
                <span class="input-group-text" translate>matchBankStatement.recipient</span>
            </div>
            <input type="text" class="form-control d-inline" (keyup.enter)="search()" formControlName="recipientFullName" appTrimWhitespace>
        </div>

        <!-- The dates get a row to themselves. An input-group aligns its children by
             stretching them to a common height, which is what keeps each calendar button the
             same height as the field it belongs to - so nothing that would override that
             (align-items-center, or a mat-form-field with its own box model) goes in here. -->
        <div class="input-group mb-2">
            <div class="input-group-prepend">
                <span class="input-group-text" id="matchDateFrom" translate>matchBankStatement.dateFrom</span>
            </div>
            <input class="form-control d-inline" style="max-width: 150pt;" aria-describedby="matchDateFrom"
                formControlName="dateFrom" ngbDatepicker #md1="ngbDatepicker" readonly>
            <div class="input-group-append">
                <button class="btn btn-outline-secondary fa fa-calendar" (click)="md1.toggle()" type="button"></button>
            </div>

            <div class="input-group-prepend ml-2">
                <span class="input-group-text" id="matchDateTo" translate>matchBankStatement.dateTo</span>
            </div>
            <input class="form-control d-inline" style="max-width: 150pt;" aria-describedby="matchDateTo"
                formControlName="dateTo" ngbDatepicker #md2="ngbDatepicker" readonly>
            <div class="input-group-append">
                <button class="btn btn-outline-secondary fa fa-calendar" (click)="md2.toggle()" type="button"></button>
            </div>

            <!-- Сума. A plain input rather than a mat-form-field, for the reason above: this
                 row relies on the input-group stretching its children to a common height. -->
            <div class="input-group-prepend ml-2">
                <span class="input-group-text" translate>matchBankStatement.totalAmount</span>
            </div>
            <input type="number" step="0.01" class="form-control d-inline" style="max-width: 120pt;"
                (keyup.enter)="search()" formControlName="totalAmount">
        </div>

        <!-- The payment types and the buttons sit outside any input-group: mat-form-field
             brings its own vertical spacing and would otherwise stretch the row around it. -->
        <div class="d-flex align-items-center mb-2">
            <mat-form-field style="min-width: 260px;">
                <mat-select multiple formControlName="paymentTypeIds"
                    placeholder="{{'matchBankStatement.paymentType' | translate}}">
                    <mat-option *ngFor="let paymentType of paymentTypes" [value]="paymentType.id">
                        {{paymentType.description}}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <button class="btn btn-outline-success ml-3" type="button" (click)="search()" translate>button.search</button>
            <button class="btn btn-outline-danger ml-2" type="button" (click)="clearSearch()" translate>button.clear</button>
        </div>
    </form>

    <!-- Results. Empty until the operator searches, so the dialog opens on a clean slate. -->
    <div *ngIf="searched">
        <div class="table-responsive">
            <table class="table table-bordered table-striped w-100" [hidden]="!dataLoaded">
                <thead>
                    <tr>
                        <th sortBy="invoiceNumber" (sort)="onSort($event)" scope="col" translate>matchBankStatement.invoiceNumber</th>
                        <th sortBy="invoiceDate" (sort)="onSort($event)" scope="col" translate>matchBankStatement.invoiceDate</th>
                        <th scope="col" translate>matchBankStatement.recipient</th>
                        <th sortBy="totalAmount" (sort)="onSort($event)" scope="col" class="text-right" translate>matchBankStatement.totalAmount</th>
                        <th scope="col" translate>matchBankStatement.paymentType</th>
                        <th scope="col" translate>matchBankStatement.status</th>
                        <th scope="col" class="text-center" translate>matchBankStatement.actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let document of content">
                        <td>{{document.invoiceNumber}}</td>
                        <td>{{document.invoiceDate}}</td>
                        <td>{{document.recipientFullName}}</td>
                        <td class="text-right text-nowrap">{{document.totalAmount | number: '1.2-2'}}</td>
                        <td>{{document.paymentType}}</td>
                        <td>
                            {{document.status}}
                            <!-- Linking a paid invoice is allowed, but nothing about its status
                                 will change - worth saying so before the operator clicks. -->
                            <i class="fa fa-info-circle text-info ml-1" *ngIf="document.alreadyPaid"
                                data-toggle="tooltip" data-placement="bottom"
                                title="{{'matchBankStatement.alreadyPaidHint' | translate}}"></i>
                        </td>
                        <td class="text-center py-2">
                            <button class="btn btn-sm btn-success" type="button"
                                [disabled]="working || isLinked(document)"
                                data-toggle="tooltip" data-placement="bottom"
                                title="{{'matchBankStatement.link' | translate}}"
                                (click)="link(document)">
                                <i class="fa" [ngClass]="isLinked(document) ? 'fa-check' : 'fa-plus'"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <ng-container *ngIf="dataLoaded; else dataLoadingTemplate"></ng-container>

        <div class="paging-wrapper d-flex justify-content-between">
            <select class="pagination-size custom-select" [(ngModel)]="sortingPaging.pageSize" [ngModelOptions]="{standalone: true}" (change)="onPageSizeChange($event)">
                <option *ngFor="let item of pageSizeOptions; let i = index" [value]="pageSizeOptions[i]">
                    {{pageSizeOptions[i]}} {{'paging.itemsPerPage' | translate}}
                </option>
            </select>
            <ngb-pagination class="pagination-control"
                [(page)]="sortingPaging.pageNumber"
                [pageSize]="sortingPaging.pageSize"
                [collectionSize]="sortingPaging.totalElements"
                [boundaryLinks]="true"
                [maxSize]="maxSize"
                (pageChange)="pageChanged($event)">
            </ngb-pagination>
            <span class="pagination-index border center">
                {{sortingPaging.fromRow}} - {{sortingPaging.toRow}} / {{sortingPaging.totalElements}}
            </span>
        </div>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button class="btn btn-outline-secondary" type="button" (click)="close()" translate>button.close</button>
</mat-dialog-actions>

<ng-template #dataLoadingTemplate>
    <div *ngIf="errorMessage$ | async as errorMessage; else dataSpinner">
        <div class="alert alert-danger">
            {{errorMessage}}
        </div>
        <div class="d-flex justify-content-center">
            <i class="fa fa-refresh" (click)="reload()"></i>
        </div>
    </div>
    <ng-template #dataSpinner>
        <div class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </ng-template>
</ng-template>
